import * as Schema from "effect/Schema";
import { EnvironmentId, TrimmedNonEmptyString } from "./baseSchemas.ts";

export const EnvironmentAccessScope = Schema.Literals([
  "environment:read",
  "operations:read",
  "operations:write",
  "environment-access:admin",
  /**
   * Another Environment's Machine Pool may start threads here and read their status, and nothing
   * else (ADR-0149). A session holding only this reaches only `/api/machine-pool/`.
   */
  "threads:delegate",
]);
export type EnvironmentAccessScope = typeof EnvironmentAccessScope.Type;

export const EnvironmentRecoveryAction = Schema.Literals(["retry", "reconfigure", "sign-in"]);
export type EnvironmentRecoveryAction = typeof EnvironmentRecoveryAction.Type;

export const EnvironmentAccessProfile = Schema.Struct({
  environmentId: EnvironmentId,
  environmentLabel: TrimmedNonEmptyString,
  endpoint: TrimmedNonEmptyString,
  applicationRelease: TrimmedNonEmptyString,
  scopes: Schema.Array(EnvironmentAccessScope),
  sessionExpiresAt: Schema.Int.check(Schema.isGreaterThan(0)),
});
export type EnvironmentAccessProfile = typeof EnvironmentAccessProfile.Type;

export const EnvironmentWebSocketTicket = Schema.String.check(
  Schema.isPattern(/^eat_[A-Za-z0-9_-]{43}$/),
);
export type EnvironmentWebSocketTicket = typeof EnvironmentWebSocketTicket.Type;

export const IssuedEnvironmentWebSocketTicket = Schema.Struct({
  ticket: EnvironmentWebSocketTicket,
  expiresAt: Schema.Int.check(Schema.isGreaterThan(0)),
});
export type IssuedEnvironmentWebSocketTicket = typeof IssuedEnvironmentWebSocketTicket.Type;

/**
 * Where a client exchanges a one-time grant the destination Environment minted for a session of
 * its own (ADR-0118). The grant is the authority, so this route carries no other credential.
 */
export const ENVIRONMENT_ACCESS_EXCHANGE_PATH = "/.well-known/afar-research/environment-access";

/**
 * Where a grant is minted. Unlike the exchange, this one is protected: minting is what an
 * already-trusted party on the machine does, which over SSH means someone who could read the
 * Host Store anyway.
 */
export const ENVIRONMENT_ACCESS_GRANT_PATH = "/.well-known/afar-research/environment-access/grant";

/**
 * Where the sessions this Environment has issued are listed and revoked.
 *
 * Protected like minting, and for the same reason: seeing and ending a client's access is
 * something a party already on the machine does. Without it, pairing would be a one-way door.
 */
export const ENVIRONMENT_ACCESS_SESSIONS_PATH =
  "/.well-known/afar-research/environment-access/sessions";
export const ENVIRONMENT_ACCESS_REVOKE_PATH =
  "/.well-known/afar-research/environment-access/sessions/revoke";

/** One issued session, as the machine's owner sees it. The credential itself is never included. */
export const EnvironmentAccessSession = Schema.Struct({
  sessionId: TrimmedNonEmptyString,
  clientLabel: TrimmedNonEmptyString,
  issuedAt: Schema.Int,
  expiresAt: Schema.Int,
  revokedAt: Schema.NullOr(Schema.Int),
});
export type EnvironmentAccessSession = typeof EnvironmentAccessSession.Type;

export const EnvironmentAccessSessionList = Schema.Struct({
  sessions: Schema.Array(EnvironmentAccessSession),
});
export type EnvironmentAccessSessionList = typeof EnvironmentAccessSessionList.Type;

export const EnvironmentAccessRevokeRequest = Schema.Struct({
  sessionId: TrimmedNonEmptyString,
});
export type EnvironmentAccessRevokeRequest = typeof EnvironmentAccessRevokeRequest.Type;

export const EnvironmentAccessRevokeResult = Schema.Struct({ revoked: Schema.Boolean });
export type EnvironmentAccessRevokeResult = typeof EnvironmentAccessRevokeResult.Type;

/**
 * What a grant confers once exchanged. An owner grant is minted only by a party that already holds
 * owner authority on the machine (the Local Host Session or an owner session), because it carries
 * `environment-access:admin`; a client grant carries the standard scopes alone.
 */
export const EnvironmentAccessGrantRole = Schema.Literals(["owner", "client", "delegate"]);
export type EnvironmentAccessGrantRole = typeof EnvironmentAccessGrantRole.Type;

export const EnvironmentAccessGrantRequest = Schema.Struct({
  role: Schema.optionalKey(EnvironmentAccessGrantRole),
});
export type EnvironmentAccessGrantRequest = typeof EnvironmentAccessGrantRequest.Type;

export const EnvironmentAccessGrantResult = Schema.Struct({
  grant: TrimmedNonEmptyString,
  expiresAt: Schema.Int.check(Schema.isGreaterThan(0)),
});
export type EnvironmentAccessGrantResult = typeof EnvironmentAccessGrantResult.Type;

/**
 * What a client signs to prove it holds the key its grant was bound to (ADR-0122).
 *
 * The grant is named by its digest rather than by its value, so a proof is useless on its own and
 * discloses nothing about the credential it accompanies. It needs no nonce of its own: the grant
 * it is presented with is single-use, so a captured proof has nothing left to redeem.
 */
export const EnvironmentAccessExchangeProof = Schema.Struct({
  operation: Schema.Literal("exchange"),
  grantDigest: TrimmedNonEmptyString,
  clientPublicKey: TrimmedNonEmptyString,
});
export type EnvironmentAccessExchangeProof = typeof EnvironmentAccessExchangeProof.Type;

export const EnvironmentAccessExchangeRequest = Schema.Struct({
  grant: TrimmedNonEmptyString,
  /** Shown to the person choosing which client to revoke. */
  clientLabel: TrimmedNonEmptyString,
  /**
   * Present when the grant was brokered by AFAR Connect and is therefore bound to a key. Absent
   * for a grant minted for a party already on the machine, which binds to nothing.
   */
  proof: Schema.optionalKey(
    Schema.Struct({
      claims: EnvironmentAccessExchangeProof,
      signature: TrimmedNonEmptyString,
    }),
  ),
});
export type EnvironmentAccessExchangeRequest = typeof EnvironmentAccessExchangeRequest.Type;

/**
 * Deliberately carries no Environment identity. The descriptor already serves that over the same
 * connection, and a second source for it is a second thing that can disagree.
 */
export const EnvironmentAccessExchangeResult = Schema.Struct({
  /**
   * Which Environment answered. A device adopting a computer learns its identity here rather than
   * from a separate lookup: the Environment that issued the session is the one that names itself,
   * so there is nothing for a second source to disagree with.
   */
  environmentId: TrimmedNonEmptyString,
  session: TrimmedNonEmptyString,
  sessionId: TrimmedNonEmptyString,
  scopes: Schema.Array(EnvironmentAccessScope),
  expiresAt: Schema.Int.check(Schema.isGreaterThan(0)),
});
export type EnvironmentAccessExchangeResult = typeof EnvironmentAccessExchangeResult.Type;

/** Why an exchange was refused. Reported as a code so a client can say something accurate. */
export const EnvironmentAccessExchangeRefusal = Schema.Literals([
  "unknown",
  "expired",
  "already-redeemed",
  "malformed",
]);
export type EnvironmentAccessExchangeRefusal = typeof EnvironmentAccessExchangeRefusal.Type;

/**
 * Browser pairing. A browser opens `/pair#token=<grant>` (the grant rides in the fragment, so it
 * never reaches a server log or a Referer), then exchanges it here for a session the Environment
 * sets as an HttpOnly cookie. Both routes are answered without a credential: the first reports
 * whether the caller already has one, and the second is how it gets one.
 */
export const ENVIRONMENT_AUTH_SESSION_PATH = "/api/auth/session";
export const ENVIRONMENT_BROWSER_SESSION_PATH = "/api/auth/browser-session";
/** Ends the caller's own session and clears its cookie: the way out of browser pairing. */
export const ENVIRONMENT_SIGN_OUT_PATH = "/api/auth/sign-out";
/**
 * Where a client holding a bearer session mints a one-use WebSocket ticket, presented as
 * `/ws?wsTicket=<ticket>`. A browser has no way to attach a bearer header to a WebSocket upgrade,
 * and a long-lived credential in a URL would outlive the connection it opened.
 */
export const ENVIRONMENT_WEBSOCKET_TICKET_PATH = "/api/auth/websocket-ticket";
export const ENVIRONMENT_WEBSOCKET_TICKET_PARAM = "wsTicket";
export const ENVIRONMENT_PAIRING_PATH = "/pair";
export const ENVIRONMENT_PAIRING_TOKEN_PARAM = "token";

export const EnvironmentAuthSessionMethod = Schema.Literals([
  "local-host",
  "bearer",
  "cookie",
  "websocket-ticket",
]);
export type EnvironmentAuthSessionMethod = typeof EnvironmentAuthSessionMethod.Type;

export const EnvironmentAuthSessionState = Schema.Struct({
  authenticated: Schema.Boolean,
  method: Schema.optionalKey(EnvironmentAuthSessionMethod),
  scopes: Schema.optionalKey(Schema.Array(EnvironmentAccessScope)),
  expiresAt: Schema.optionalKey(Schema.Int),
});
export type EnvironmentAuthSessionState = typeof EnvironmentAuthSessionState.Type;

export const EnvironmentBrowserSessionRequest = Schema.Struct({
  credential: TrimmedNonEmptyString,
});
export type EnvironmentBrowserSessionRequest = typeof EnvironmentBrowserSessionRequest.Type;

export const EnvironmentBrowserSessionResult = Schema.Struct({
  authenticated: Schema.Literal(true),
  scopes: Schema.Array(EnvironmentAccessScope),
  expiresAt: Schema.Int.check(Schema.isGreaterThan(0)),
});
export type EnvironmentBrowserSessionResult = typeof EnvironmentBrowserSessionResult.Type;

/** Every route a Machine Pool's delegated session may call, and the only ones. */
export const MACHINE_POOL_DELEGATION_PATH_PREFIX = "/api/machine-pool/";
